import { useCallback, useEffect, useState } from 'react';

import copy from '~/assets/icons/copy.svg';
import { ButtonColors } from '~/components/ui/Button';
import { rage } from '~/rage';

import {
	ButtonRow,
	ClearButton,
	CodeTextarea,
	ContentContainer,
	StyledButton,
} from '../../components/CodeEditor';
import {
	HeaderContainer,
	StyledCloseButton,
	Title,
} from '../../components/Header';
import { PanelContainer } from '../../components/Panel';
import {
	CopyButton,
	ErrorText,
	NoOutputText,
	ResultContainer,
	ResultHeader,
	ResultLabel,
	ResultText,
} from '../../components/ResultOutput';
import { useRuncodeState } from '../../hooks';

const Runcode: React.FC = () => {
	const {
		code,
		result,
		error,
		isExecuting,
		handleCodeChange,
		handleExecuteClient,
		handleExecuteServer,
		handleClear,
	} = useRuncodeState();

	const [closeButtonHover, setCloseButtonHover] = useState(false);
	const output = error || result;

	const handleClose = useCallback(() => {
		rage.runcode.exitMenu();
	}, []);

	const handleCopyOutput = useCallback(() => {
		if (!output) {
			return;
		}

		rage.input.copyToClipboard(output);
		rage.notifications.showInfo('Текст скопирован в буфер обмена');
	}, [output]);

	const handleCloseMouseEnter = useCallback(
		() => setCloseButtonHover(true),
		[],
	);

	const handleCloseMouseLeave = useCallback(
		() => setCloseButtonHover(false),
		[],
	);

	const handleCodeChangeWrapper = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			handleCodeChange(e.target.value);
		},
		[handleCodeChange],
	);

	useEffect(() => {
		rage.input
			.setAnyInputFocused(true)
			.catch((err) => rage.notifications.showException(err));

		return () => {
			rage.input
				.setAnyInputFocused(false)
				.catch((err) => rage.notifications.showException(err));
		};
	}, []);

	return (
		<PanelContainer>
			<HeaderContainer>
				<Title>Запуск кода</Title>
				<StyledCloseButton
					onClick={handleClose}
					$hover={closeButtonHover}
					onMouseEnter={handleCloseMouseEnter}
					onMouseLeave={handleCloseMouseLeave}
				/>
			</HeaderContainer>
			<ContentContainer>
				<CodeTextarea
					value={code}
					onChange={handleCodeChangeWrapper}
					placeholder="Ваш код здесь..."
					spellCheck={false}
				/>
				<ButtonRow>
					<ClearButton onClick={handleClear} disabled={isExecuting}>
						Очистить
					</ClearButton>
					<StyledButton
						$color={ButtonColors.Select2}
						onClick={handleExecuteServer}
						disabled={isExecuting}
					>
						На сервере
					</StyledButton>
					<StyledButton
						$color={ButtonColors.Buy}
						onClick={handleExecuteClient}
						disabled={isExecuting}
					>
						На клиенте
					</StyledButton>
				</ButtonRow>
				<ResultContainer>
					<ResultHeader>
						<ResultLabel>Вывод</ResultLabel>
						<CopyButton
							type="button"
							onClick={handleCopyOutput}
							disabled={!output}
							title="Скопировать вывод"
							aria-label="Скопировать вывод"
						>
							<img src={copy} alt="" />
						</CopyButton>
					</ResultHeader>
					{error ? (
						<ErrorText>{error}</ErrorText>
					) : result ? (
						<ResultText>{result}</ResultText>
					) : (
						<NoOutputText>Нет вывода</NoOutputText>
					)}
				</ResultContainer>
			</ContentContainer>
		</PanelContainer>
	);
};

export { Runcode };
